import { Paper, Stack, Typography, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import CompletedReport from './reportStatistics/completedReports';
import FinalResponseDeadline from './reportStatistics/finalResponseDeadline';
import FirstResponseDeadline from './reportStatistics/firstResponseDeadline';
import ReportCompletion from './reportStatistics/reportCompletion';
import RequestForInformationCount from './reportStatistics/requestForInformationCount';
import UnopenedReport from './reportStatistics/unopenedReports';

import { DEFAULT_VALUE, INITIAL_VALUE } from '../../../contexts/partner/partnerContext';
import LoadingComponent from '../../common/loadingComponent';

import { useIntakeChannel } from '~/slices/intakeChannelSlice';

interface ReportStatisticsProps {
  tenantId: number;
}
interface ReportStatisticsObject {
  firstResponseCount: number;
  finalResponseCount: number;
  unopened: number;
  complete: number;
  avgTimeCompletion: string;
  rfiCount: number;
}

interface ReportStatisticsFromAPI {
  first_response_count: number;
  final_response_count: number;
  unopened: number;
  completed: number;
  avg_time_completion: string;
  rfi_count: number;
}

const transformDataFromAPI = (data: ReportStatisticsFromAPI) => {
  return {
    firstResponseCount: data.first_response_count,
    finalResponseCount: data.final_response_count,
    unopened: data.unopened,
    complete: data.completed,
    avgTimeCompletion: data.avg_time_completion,
    rfiCount: data.rfi_count,
  };
};

const ReportStatistics = ({ tenantId }: ReportStatisticsProps) => {
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [ReportsCount, setReportCount] = useState<ReportStatisticsObject>({
    firstResponseCount: 0,
    finalResponseCount: 0,
    unopened: 0,
    complete: 0,
    avgTimeCompletion: '',
    rfiCount: 0,
  });

  const { data, error, isLoading } = useSWR(
    `dashboard/reports-count${
      !(tenantId === INITIAL_VALUE || tenantId === DEFAULT_VALUE) ? `?tenant_id=${tenantId}&intake_channel=${selectedChannel}` : ''
    }`,
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (!isLoading && !error && data) {
      setReportCount(transformDataFromAPI(data));
    }
  }, [data, error, isLoading]);

  const TOTAL_REPORT_COMPONENTS = [
    {
      key: 'first-response-deadline',
      component: (
        <FirstResponseDeadline value={ReportsCount?.firstResponseCount || 0} />
      ),
    },
    {
      key: 'final-response-deadline',
      component: (
        <FinalResponseDeadline value={ReportsCount?.finalResponseCount || 0} />
      ),
    },
    {
      key: 'completed-report',
      component: <CompletedReport value={ReportsCount?.complete || 0} />,
    },
    {
      key: 'unopened-report',
      component: <UnopenedReport value={ReportsCount?.unopened || 0} />,
    },
    {
      key: 'report-completion',
      component: (
        <ReportCompletion value={ReportsCount?.avgTimeCompletion || ''} />
      ),
    },
    {
      key: 'request-for-information',
      component: (
        <RequestForInformationCount value={ReportsCount?.rfiCount || 0} />
      ),
    },
  ];

  return (
    <>
      {isLoading && (
        <Stack
          alignItems='center'
          sx={{
            width: '100%',
            height: '125px',
          }}
        >
          <LoadingComponent />
        </Stack>
      )}
      {error && (
        <Typography
          sx={{
            fontSize: '14px',
            color: 'red',
            textAlign: 'center',
          }}
        >
          {error.message}
        </Typography>
      )}
      {!isLoading && !error && ReportsCount && (
        <Grid
          container
          direction='row'
          component={Paper}
          elevation={0}
          variant='outlined'
          sx={{
            border: '1px solid #D6D6D6',
            borderRadius: 2,
            boxShadow: '0px 0px 4px 0px #0000000A',
          }}
        >
          {TOTAL_REPORT_COMPONENTS.map((item, index) => (
            <Grid
              item
              xs={4}
              key={item.key}
              sx={{
                borderLeft: index % 3 !== 0 ? '1px solid #D6D6D6' : '0',
                borderBottom:
                  index !== TOTAL_REPORT_COMPONENTS.length - 3 &&
                  index !== TOTAL_REPORT_COMPONENTS.length - 2 &&
                  index !== TOTAL_REPORT_COMPONENTS.length - 1
                    ? '1px solid #D6D6D6'
                    : 0,
              }}
            >
              {item.component}
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default ReportStatistics;
